export namespace UrlUtils {
    type QueryParameterValue = string | number | boolean | null | undefined;

    /**
     * Retrieves the value of a query parameter from the specified URL.
     *
     * @param name - The name of the query parameter to retrieve.
     * @param {string} [url=null] - The URL to retrieve the query parameter from. If not provided, the current URL is used.
     * @param {boolean} [caseSensitive=true] - Indicates whether the search should be case-sensitive.
     * @param {boolean} [replacePlusWithSpace=true] - Indicates whether '+' characters should be replaced with spaces.
     * @return {string | null} - The value of the query parameter, or null if it is not found.
     */
    export const getQueryString = (
        name: string,
        url: string | null = null,
        caseSensitive = true,
        replacePlusWithSpace = true
    ): string | null => {
        if (!url) url = window.location.href;
        name = name.replace(/[[\]]/g, "\\$&");
        const expression = "[?&]" + name + "(=([^&#]*)|&|#|$)";
        const regex = caseSensitive ? new RegExp(expression) : new RegExp(expression, "i");
        const results = regex.exec(url);
        if (!results) return null;
        if (!results[2]) return "";
        const replacedResult = replacePlusWithSpace ? results[2].replace(/\+/g, " ") : results[2];
        return decodeURIComponent(replacedResult);
    };

    /**
     * Clears the specified URL parameters from the current URL and updates the browser history.
     *
     * @param {string[]} urlParams - An optional array of URL parameters to clear. If not provided, all URL parameters will be cleared.
     * @return {void}
     */
    export const clearUrlParams = (urlParams?: string[]): void => {
        let newUrl = window.location.href;

        // If no parameters are provided, clear all parameters
        if (urlParams === undefined) {
            const url = new URL(window.location.href);
            url.search = "";
            url.hash = "";
            newUrl = url.toString();
        } else if (urlParams.length > 0) {
            // Clear the specified parameters
            for (const element of urlParams) {
                const parameter = element;
                newUrl = newUrl
                    .replace(new RegExp("[?&]" + parameter + "=[^&#]*(#.*)?$"), "$1")
                    .replace(new RegExp("([?&])" + parameter + "=[^&]*&"), "$1");
            }
        } else {
            // No parameters to clear
            return;
        }

        if (window.history) {
            window.history.replaceState(null, document.title, newUrl);
        }
    };

    /**
     * Calls clearUrlParams function with the provided urlParam.
     *
     * @param {string} urlParam - The URL parameter to clear.
     * @return {void}
     */
    export const clearUrlParam = (urlParam: string) => {
        clearUrlParams([urlParam]);
    };

    /**
     * Retrieves the URL without any query parameters.
     *
     * @returns {string} - The URL without any query parameters.
     */
    export const getUrlWithoutParams = () => {
        return window.location.href.split("?")[0];
    };

    export const removeUrlPath = (urlPath: string): void => {
        if (!window.location.href.includes(urlPath)) {
            return;
        }

        let newUrl = window.location.href;
        newUrl = newUrl.replace(urlPath, "");

        if (window.history) {
            window.history.replaceState(null, document.title, newUrl);
        }
    };

    /**
     * Reads URL parameters from a given URL.
     *
     * @param url {string} - The URL to read parameters from.
     * @param responseMode {"query" | "fragment"} - The mode to read parameters from, either "query" or "fragment". Default is "query".
     * @returns {URLSearchParams} - The URLSearchParams object containing the parameters.
     */
    export const readParams = (url: string, responseMode: "query" | "fragment" = "query"): URLSearchParams => {
        if (!url) throw new TypeError("Invalid URL");
        // the base URL is irrelevant, it's just here to support relative url arguments
        const parsedUrl = new URL(url, "http://127.0.0.1");
        const params = parsedUrl[responseMode === "fragment" ? "hash" : "search"];
        return new URLSearchParams(params.slice(1));
    };

    /**
     * Add query parameters to a URL, preserving existing query and hash.
     *
     * @example
     * addQueryParameters("/media/image.jpg", { w: 320, h: 180 });
     * // Returns: "/media/image.jpg?w=320&h=180"
     *
     * @param url {string}
     * @param queryParameters {Record<string, QueryParameterValue> | null | undefined}
     * @returns {string}
     */
    export const addQueryParameters = (
        url: string,
        queryParameters: Record<string, QueryParameterValue> | null | undefined
    ): string => {
        if (!url) {
            return "";
        }

        if (!queryParameters || Object.keys(queryParameters).length === 0) {
            return url;
        }

        if (url.startsWith("./") || url.startsWith("../")) {
            const [withoutHash, hash = ""] = url.split("#", 2);
            const [path, query = ""] = withoutHash.split("?", 2);
            const params = new URLSearchParams(query);

            for (const [key, value] of Object.entries(queryParameters)) {
                if (value === null || value === undefined || value === "") {
                    continue;
                }

                params.set(key, String(value));
            }

            const search = params.toString();
            const queryPart = search ? "?" + search : "";
            const hashPart = hash ? "#" + hash : "";

            return `${path}${queryPart}${hashPart}`;
        }

        // Placeholder origin for parsing relative URLs with URL(); no request is made.
        const baseUrl = "http://127.0.0.1";
        const isProtocolRelative = url.startsWith("//");
        const isAbsolute = isProtocolRelative || /^[a-z][a-z\d+\-.]*:/i.test(url);
        const parsedUrl = new URL(url, baseUrl);

        for (const [key, value] of Object.entries(queryParameters)) {
            if (value === null || value === undefined || value === "") {
                continue;
            }

            parsedUrl.searchParams.set(key, String(value));
        }

        if (isProtocolRelative) {
            return parsedUrl.href.replace(/^http:/, "");
        }

        if (isAbsolute) {
            return parsedUrl.toString();
        }

        if (url.startsWith("/")) {
            return `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
        }

        return `${parsedUrl.pathname.slice(1)}${parsedUrl.search}${parsedUrl.hash}`;
    };

    export const trimEndSlash = (s: string) => {
        return s?.replace(/\/+$/, "");
    };

    export const addQueryParameter = (url: string, queryParameter: string): string => {
        const queryParameterSeparator = url.includes("?") ? "&" : "?";
        const urlAnchorIndex = url.indexOf("#");

        if (urlAnchorIndex > -1) {
            return (
                url.slice(0, urlAnchorIndex) + `${queryParameterSeparator}${queryParameter}` + url.slice(urlAnchorIndex)
            );
        }

        return `${url}${queryParameterSeparator}${queryParameter}`;
    };
}
